import { useState } from "react";
import { authPost, errorMessage } from "../lib/authApi.ts";

// The sign-in-with-SSO form. Starts the Organisation's provider flow through
// the auth library and sends the browser to the URL it answers with.
// See ADR-0042.

type SsoSignInFormProps = {
  initialSlug?: string;
  // Test seam: where to send the browser. Defaults to window.location.
  navigate?: (url: string) => void;
};

/**
 * Asks for an Organisation slug and sends the browser to that Organisation's
 * identity provider. Rendered by the public entry at `/login/sso`.
 */
export function SsoSignInForm({
  initialSlug = "",
  navigate = (url) => window.location.assign(url),
}: SsoSignInFormProps): JSX.Element {
  const [slug, setSlug] = useState(initialSlug);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = await authPost<{ url?: string; redirect?: boolean }>("/sign-in/sso", {
        organizationSlug: slug.trim(),
        callbackURL: "/",
        errorCallbackURL: "/login/sso?error=sso",
      });
      if (body?.url) {
        navigate(body.url);
        return;
      }
      setError("The identity provider gave no address to send you to.");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-card">
      <form onSubmit={onSubmit} aria-label="Sign in with SSO">
        <h1>Sign in with SSO</h1>
        <p className="lede">
          Type your Organisation's slug — the short name in the link your admin
          shared — and we will send you to your company's identity provider.
        </p>
        <div className="field">
          <label htmlFor="sso-slug">Organisation slug</label>
          <input
            id="sso-slug"
            type="text"
            name="organizationSlug"
            className="mono"
            required
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
        </div>
        {error ? (
          <div className="notice bad" role="alert">
            <div>{error}</div>
          </div>
        ) : null}
        <div className="btn-row">
          <button type="submit" className="btn primary lg block" disabled={submitting}>
            {submitting ? "Redirecting…" : "Continue"}
          </button>
        </div>
        <p className="hint">
          <a href="/login">Sign in with a password instead</a>
        </p>
      </form>
    </div>
  );
}
